import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Chip, Divider, Grid, Link, Typography, styled, useTheme, type GridProps } from '@mui/material';
import type { BoxProps } from '@mui/material/Box';
import type { ChipProps } from '@mui/material/Chip';
import type { TypographyProps } from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import _ from 'lodash';
import type { FC, HTMLAttributes, PropsWithChildren } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import AmexLogo from '../../../../../../assets/img/amex-icon.svg';
import ApprovedCardImage from '../../../../../../assets/img/gocard-bg.svg';
import Commercial_Credit_Digital_Wallet from '../../../../../../assets/img/hsbc/Commercial_Credit_Digital_Wallet.svg';
import { virtualCardTypes } from '../../../../constants/appConstants';
import { companyPropertiesQuery } from '../../../../queries/companyQuery';
import { virtualCardQuery, type CardProvider } from '../../../../queries/paymentQuery';
import type { ConversationEvent } from '../../../../repositories/conversationRepository';
import type { VirtualCardDataApiType, VirtualCardDataType } from '../../../../repositories/paymentRepository';
import { formatDate } from '../../../../utils/dateUtils';
import { getCancelledWord } from '../../../../utils/translationUtil';
import { useZustand } from '../../../../zustandStore';
import { cvv2OrCvc, expirationLabel } from '../../wallet/walletDetails/walletCard';
import type { Requester } from '../newRequest/requestDetail';

const frequencies = {
	DAILY: {
		timePeriodDifference: 1,
		unit: 'day',
	},
	MONTHLY: {
		timePeriodDifference: 1,
		unit: 'month',
	},
	QUARTERLY: {
		timePeriodDifference: 3,
		unit: 'month',
	},
	WEEKLY: {
		timePeriodDifference: 1,
		unit: 'week',
	},
	YEARLY: {
		timePeriodDifference: 1,
		unit: 'year',
	},
};

export const StyledCard = styled(Box)<BoxProps>(ownerState => {
	const { theme } = ownerState;
	const backgroundImage = globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
		? Commercial_Credit_Digital_Wallet
		: ApprovedCardImage;

	return {
		backgroundImage: `url(${backgroundImage})`,
		backgroundSize: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? 'contain !important' : 'cover',
		borderRadius: '10px',
		backgroundRepeat: 'no-repeat',
		height: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? '184px' : '204px',
		width: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? '277px' : '320px',
		maxWidth: '100%',
		marginBottom: '10px',
		padding: '18px 17px 10px 17px',
		color: theme.palette.tsGoColor.grayLightest,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		overflow: 'hidden',
		position: 'relative',
		...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
			? {
					borderRadius: 0,
					marginTop: '10px',
				}
			: {}),
	};
});

const AnimatedWallet = styled(Box)<BoxProps>(() => {
	return {
		backgroundSize: '200%',
		backgroundRepeat: 'no-repeat',
		borderRadius: '10px',
		height: '155px',
		maxWidth: '100%',
		minWidth: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? '270px' : '320px',
		width: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? '100%' : '320px',
		padding: '18px 17px 10px 17px',
		marginBottom: '10px',
		animation: 'preload 2s infinite linear',
		backgroundColor: 'hsl(204, 22%, 95%)',
		backgroundImage:
			'linear-gradient(90deg, hsl(204, 22%, 95%) 0%, hsl(204, 22%, 95%) 40%, hsl(198, 20%, 83%) 50%, hsl(204, 22%, 95%) 60%, hsl(204, 22%, 95%) 100%)',
		...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
			? {
					borderRadius: 0,
					marginTop: '10px',
				}
			: {}),
	};
});

export const StyledBox = styled(Box)<BoxProps>(() => ({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	width: '100%',
}));

const StyledWallet = styled(Box)<BoxProps>(ownerState => {
	const { theme } = ownerState;
	return {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'center',
		padding: 1,
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
	};
});

export const StyledElement = styled(Box)<BoxProps>(() => ({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'row',
	flexGrow: '1',
	...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
		? {
				alignItems: 'baseline',
			}
		: {}),
}));

export const ExpirationLabel = styled(Typography)<TypographyProps>(ownerState => {
	const { theme } = ownerState;
	return {
		color: theme.palette.tsGoColor.black,
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.mini),
		fontWeight: theme.typography.pxToRem(theme.typography.tsFontWeight.default),
		lineHeight: '12px',
		maxWidth: '30px',
		...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
			? {
					fontSize: '12px',
					fontWeight: 400,
					color: '#FFFFFF',
					textShadow: '1px 1px 1.9px rgba(0, 0, 0, 1)',
					maxWidth: 'none',
				}
			: {}),
	};
});

export const ExpirationValue = styled(Typography)<TypographyProps>(ownerState => {
	const { theme } = ownerState;
	return {
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
		padding: '10px 5px',
		textShadow: '0 1px 2px rgba(0, 0, 0, 0.36)',
		...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
			? {
					padding: '0 5px',
					fontSize: '18px',
					fontWeight: 400,
					color: '#FFFFFF',
					textShadow: '1px 1px 1.9px rgba(0, 0, 0, 0.7)',
				}
			: {}),
	};
});

export const StyledChip = styled(Chip)<ChipProps>(ownerState => {
	const { color, theme } = ownerState;
	return {
		borderRadius: '3px',
		backgroundColor: color,
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.mini),
		fontWeight: theme.typography.tsFontWeight.bold,
		left: '0',
		position: 'absolute',
		top: '20px',
		color: theme.palette.tsGoColor.white,
		...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
			? {
					borderRadius: 0,
				}
			: {}),
	};
});

export const StyledHoverText = styled(Box)<BoxProps>(() => ({
	right: '17px',
	position: 'absolute',
	top: '50px',
}));

export const StyledGrid = styled(Grid)<GridProps>(() => {
	return {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		width: '80%',
		textShadow: '1px 1px hsl(204, 25%, 8%) !important',
		letterSpacing: '2.5px',
		marginTop: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? '50px' : '53px',
		':not(:last-child)': {
			color: 'transparent !important',
			textShadow: 'hsl(0, 0%, 100%) 0 0 10px !important',
			transition: 'text-shadow 0.3s',

			' > p:not(:last-of-type)': {
				color: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? 'transparent !important' : '',
				textShadow: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? 'inherit !important' : '',
				filter: 'blur(8px) !important',
			},

			' > p:last-of-type': {
				textShadow: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
					? 'inherit !important'
					: '1px 1px hsl(204, 25%, 8%) !important',
				color: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
					? 'transparent !important'
					: 'hsl(0, 0%, 100%) !important',
			},

			'&.isRevealDetailsAllowed:hover': {
				textShadow: '1px 1px hsl(204, 25%, 8%) !important',
				color: 'hsl(0, 0%, 100%) !important',
				filter: 'none !important',

				' > p:not(:last-of-type)': {
					textShadow: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? '1px 1px hsl(204, 25%, 8%) !important' : '',
					color: 'hsl(0, 0%, 100%) !important',
					filter: 'none !important',
				},

				' > p:last-of-type': {
					textShadow: '1px 1px hsl(204, 25%, 8%) !important',
					color: 'hsl(0, 0%, 100%) !important',
				},
			},
		},
	};
});
type ProviderImageProps = HTMLAttributes<HTMLElement> & {
	provider: string;
};

export const ProviderImage = styled(Box)<ProviderImageProps>({
	backgroundRepeat: 'no-repeat',
	height: '40px',
	width: '40px',
	maxHeight: '40px',
	background: '',
	variants: [
		{
			props: {
				provider: 'AMEX',
			},
			style: {
				background: `url(${AmexLogo})`,
			},
		},
	],
});

export function getCardNumber(card: VirtualCardDataType | undefined) {
	if (!card) {
		return '***********';
	}
	const lastFourdigits = card.number?.slice(-4) || card.lastDigits || '';
	const lastDigits = '***********' + (lastFourdigits || '*****');
	return card.number || lastDigits;
}

export function getCVVNumber(card: VirtualCardDataType | undefined, cardProvider: CardProvider) {
	const asterisks = cardProvider === 'AMEX' ? '****' : '***';
	if (!card) {
		return asterisks;
	}
	return card.cvv || asterisks;
}

export function formatCardNumber(card) {
	const cardNumber = getCardNumber(card);
	const lastFourDigits = cardNumber.slice(-4);
	const cardLength = cardNumber.length - 4;
	let formattedSegments: string[];

	switch (card?.provider || card?.info?.provider) {
		case 'AMEX': {
			const firstSegmentLength = 4;
			const middleSegmentLength = cardLength - firstSegmentLength;
			formattedSegments = [
				cardNumber.slice(0, firstSegmentLength),
				cardNumber.slice(firstSegmentLength, firstSegmentLength + middleSegmentLength),
				lastFourDigits,
			];
			break;
		}
		case 'HSBC': {
			const segmentLength = Math.floor(cardLength / 3);
			const remainder = cardLength % 3;
			formattedSegments = [
				cardNumber.slice(0, segmentLength + (remainder > 0 ? 1 : 0)),
				cardNumber.slice(segmentLength + (remainder > 0 ? 1 : 0), 2 * segmentLength + (remainder > 1 ? 1 : 0)),
				cardNumber.slice(2 * segmentLength + (remainder > 1 ? 1 : 0), -4),
				lastFourDigits,
			];
			break;
		}
		default: {
			const segmentLength = Math.floor(cardLength / 2);
			formattedSegments = [cardNumber.slice(0, segmentLength), cardNumber.slice(segmentLength, -4), lastFourDigits];
		}
	}

	return formattedSegments;
}

function isUserAssigned({ event, userId }) {
	return _.includes(event?.data?.task?.assignees, userId);
}

export function getVirtualCard(card: VirtualCardDataApiType | undefined) {
	return card && card.info
		? {
				balance: card.info.balance,
				billingAddress: card.info.billingAddress,
				cvv: card.cvv,
				cardId: card.info.id,
				cardNumber: card.number, // used on my request
				number: card.number, // used on my cards(back-end sends number)
				cardType: card.info.cardType ?? card.cardType,
				cardStatus: card.info.status ?? card.status,
				daysRemaining: getDaysRemaining(card),
				// only this should be use to show the expiry date on the card
				expirationDate: card.expiry,
				provider: card.info.provider,
				status: card.info.status,
				validEndingOn: card.info.validEndingOn,
				value: card.info.value,
				frequency: card.info.frequency,
				currency: card.info.currency,
				// TODO: commented out to see if it overrides in a wrong way this value
				// createdAt: card.info.createdAt,
			}
		: card;
}

function getEndOfValidityPeriod(card) {
	if (card?.validEndingOn) {
		const parts = card.validEndingOn.split('-');
		// JS expects date month when January = 0, so need to subtract 1 from month in date string
		return new Date(parts[0], Number(parts[1]) - 1, parts[2]);
	}
	if (card?.expirationDate) {
		// using expiration date for end of validity period
		return new Date(card.expirationDate.slice(0, 4), card.expirationDate.slice(4, 6));
	}

	return undefined;
}

function getDaysRemaining(card: VirtualCardDataType | undefined) {
	if (!card) {
		return '';
	}
	const DAY_IN_MILLISECONDS = 1000 * 60 * 60 * 24;
	const endOfValidityPeriod = getEndOfValidityPeriod(card);
	if (!endOfValidityPeriod) {
		return;
	}
	const timeRemaining = endOfValidityPeriod.getTime() - Date.now();
	return Math.floor(timeRemaining / DAY_IN_MILLISECONDS);
}

function isExpired(card) {
	return card?.daysRemaining < 0;
}

function isCanceled(card) {
	return card?.status === 'CANCELED' ||
		card?.status === 'UNAVAILABLE' ||
		card?.status === 'CREATION_ERROR' ||
		card?.status === 'UNKNOWN'
		? true
		: !isExpired(card) && card?.status === 'EXPIRED';
}

export function isActive(card) {
	return !isExpired(card) && !isCanceled(card);
}

function getStatusLabel(card) {
	if (isExpired(card)) {
		return 'Expired';
	} else if (isCanceled(card)) {
		return getCancelledWord('Canceled');
	} else {
		return 'Active';
	}
}

export function getVirtualCardTypeLabel({ card, fields }) {
	if (!card) {
		return '';
	}
	const cardTypeLabel = virtualCardTypes.find(({ cardsKey }) => cardsKey === card?.cardType)?.label;
	if (card?.cardType === 'RECURRING_USE') {
		// Just in case there is no default frequency state to key into we add it here
		const frequency = card.frequency || fields.frequency;
		const frequencyLabel = frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase() : '';
		return frequencyLabel + ' ' + cardTypeLabel;
	} else {
		return cardTypeLabel;
	}
}

export const getCardValidEndingOnDate = (expiryDate: string | undefined | null) => {
	if (!expiryDate) {
		return '';
	}

	return formatDate(expiryDate);
};

export function getVirtualCardRenewal(card) {
	if (!card) {
		return '';
	}
	const issueDate = dayjs(card.createdAt);
	const frequency = frequencies[card.frequency];

	if (!frequency) {
		return '';
	}
	const today = dayjs();

	// Get current difference since last replenishment period
	const differenceByFreq = today.diff(issueDate, frequency.unit) % frequency.timePeriodDifference;
	// Get remaining difference until next replenishment period
	const differenceRemaining = frequency.timePeriodDifference - differenceByFreq;
	// Get total difference to add to current date to display next replenish date
	const difference = today.diff(issueDate, frequency.unit) + differenceRemaining;
	return issueDate.add(difference, frequency.unit).format('MMM D, YYYY');
}

export function getVirtualCardStatusAndLabel({ card, fields }) {
	if (!card) {
		return '';
	}
	const statusLabel = isExpired(card) || isCanceled(card) ? `${getStatusLabel(card)} - ` : '';
	return statusLabel + getVirtualCardTypeLabel({ card, fields });
}

export const getFormattedExpiration = (expiryDate: string | undefined | null) => {
	if (!expiryDate) {
		return '';
	}

	// for hsbc we use this format "2025-05"
	// and not the "202505" format used by default
	if (expiryDate.includes('-')) {
		const [year, month] = expiryDate.split('-');
		return `${month}/${year}`;
	}

	return expiryDate.slice(4, 6) + '/' + expiryDate.slice(0, 4);
};

function getFullName(user: Requester): string {
	const fullName = `${user.firstName || ''}${user.firstName && user.lastName ? ' ' : ''}${user.lastName || ''}`.trim();
	if (!fullName) {
		return user.email;
	}
	return fullName;
}

type VirtualCardProps = HTMLAttributes<HTMLElement> & {
	event: ConversationEvent;
};

const VirtualCard: FC<PropsWithChildren<VirtualCardProps>> = ({ event }) => {
	const navigate = useNavigate();
	const { user, request } = useZustand(state => ({
		...state,
	}));
	const { purchaseRequest } = request;
	const [copyDetails, setCopyDetails] = useState({ isCopied: false, copySuccessMessage: '' });
	const [isHovering, setIsHovering] = useState(false);
	const purchaseRequestId = purchaseRequest.requestId;
	const { data: companyPropertiesData } = useQuery(
		companyPropertiesQuery(user.companyAccountId, { cardprovider: 'tsgo-cardprovider' }),
	);
	const isCardOwner = isUserAssigned({ event, userId: user?.userId });
	const { data: card } = useQuery(
		virtualCardQuery({
			documentId: purchaseRequestId,
			cardProvider: companyPropertiesData?.cardprovider,
		}),
	);
	const isAmexCardProvider = companyPropertiesData?.cardprovider === 'AMEX';
	const virtualCard = getVirtualCard(card);
	const taskId = event.data.task?.id || '';
	const theme = useTheme();
	const fields = event.data.task?.context.request.fields;
	const copyItems = {
		number: {
			name: 'card number',
			data: getCardNumber(virtualCard),
		},
		cvc: {
			name: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? 'cvv2' : 'cvc',
			data: getCVVNumber(virtualCard, companyPropertiesData?.cardprovider || ''),
		},
	};
	const formattedExpirationDate = getFormattedExpiration(virtualCard?.expirationDate || '');
	const cardNumber = getCardNumber(virtualCard);
	const isCopyAllowed = !Number.isNaN(Number(cardNumber));

	const setCopyItem = (copyItemKey, event) => {
		event.stopPropagation();
		if (!copyItems[copyItemKey] || !isCopyAllowed) {
			return;
		}
		const copyItem = copyItems[copyItemKey];
		setIsHovering(true);
		setCopyDetails({
			isCopied: false,
			copySuccessMessage: 'Copied ' + copyItem.name + '!',
		});
	};

	async function onCopySuccess(copyItemKey, event) {
		event.stopPropagation();
		if (!copyItems[copyItemKey] || !isCopyAllowed) {
			return;
		}
		const copyItem = copyItems[copyItemKey];
		await navigator.clipboard.writeText(copyItem.data);
		setCopyDetails({
			isCopied: true,
			copySuccessMessage: 'Copied ' + copyItem.name + '!',
		});
	}

	function fetchMessage() {
		return copyDetails.isCopied ? copyDetails.copySuccessMessage : '';
	}

	return (
		<>
			{!isCardOwner || virtualCard?.provider ? (
				<StyledCard
					onMouseOver={event => setCopyItem('number', event)}
					onMouseOut={() => setIsHovering(false)}
					onClick={event => onCopySuccess('number', event)}
					sx={{ cursor: isCopyAllowed && isHovering ? 'pointer' : 'default' }}
				>
					{virtualCard?.status && isAmexCardProvider ? (
						<StyledChip
							label={getVirtualCardStatusAndLabel({ card: virtualCard, fields })}
							color={isActive(virtualCard) ? 'success' : 'error'}
						/>
					) : null}
					<StyledHoverText>{isCopyAllowed && isHovering ? fetchMessage() : ''}</StyledHoverText>
					<StyledGrid className={isCopyAllowed ? 'isRevealDetailsAllowed' : ''}>
						{formatCardNumber(virtualCard).map((cardNumberPiece, index) => (
							<Typography key={cardNumberPiece + index} sx={{ fontSize: '18px' }}>
								{cardNumberPiece}
							</Typography>
						))}
						{isCopyAllowed && (
							<ContentCopyIcon
								data-testid="copy-card-number-icon"
								sx={{
									fontSize: theme.typography.pxToRem(14),
									color: 'hsl(0, 0%, 100%)',
									marginLeft: theme.tsGoUnit.tsUnitQuarter,
								}}
							/>
						)}
					</StyledGrid>
					<StyledBox>
						{formattedExpirationDate ? (
							<StyledElement>
								{expirationLabel ? <ExpirationLabel>{expirationLabel}</ExpirationLabel> : null}
								<ExpirationValue>{getFormattedExpiration(virtualCard?.expirationDate || '')}</ExpirationValue>
							</StyledElement>
						) : null}
						<StyledElement
							onMouseOver={event => setCopyItem('cvc', event)}
							onMouseOut={() => setIsHovering(false)}
							onClick={event => onCopySuccess('cvc', event)}
							sx={{ display: 'flex', justifyContent: 'flex-end' }}
						>
							<ExpirationLabel>{cvv2OrCvc}</ExpirationLabel>
							<ExpirationValue>{getCVVNumber(virtualCard, companyPropertiesData?.cardprovider || '')}</ExpirationValue>
							{isCopyAllowed && (
								<ContentCopyIcon
									data-testid="copy-card-cvc-icon"
									sx={{ fontSize: theme.typography.pxToRem(14), color: 'hsl(0, 0%, 100%)' }}
								/>
							)}
						</StyledElement>
					</StyledBox>
					<Grid container sx={{ display: 'flex', justifyContent: 'space-between' }}>
						<Grid size={10}>
							<Typography
								style={{
									...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
										? {
												fontSize: '12px',
												fontWeight: 400,
												color: '#FFFFFF',
												textShadow: '1px 1px 1.9px rgba(0, 0, 0, 0.7)',
											}
										: {}),
								}}
								data-testid="requester"
							>
								{getFullName(purchaseRequest.requester)}
							</Typography>
						</Grid>
						{globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? (
							<Grid size={2}>{virtualCard?.provider ? <ProviderImage provider={virtualCard.provider} /> : null}</Grid>
						) : null}
					</Grid>
				</StyledCard>
			) : (
				<AnimatedWallet />
			)}
			{isCardOwner ? (
				<>
					<Divider />
					<StyledWallet>
						<Link
							data-testid="wallet"
							onClick={() => {
								void navigate(`/wallet/${taskId}`);
							}}
							underline="none"
						>
							{'View in wallet'}
						</Link>
					</StyledWallet>
				</>
			) : null}
		</>
	);
};

export default VirtualCard;
